/**
 * toast.ts — storefront notify strip (parity admin #admin-notify-strip-floating)
 * + showToast info wrapper (reviews/auth and auth-standalone).
 */

export type FlashVariant = 'success' | 'error' | 'info';

/** Sync with admin footer AUTO_HIDE_MS (temporarily 10 min for success and error). */
const AUTO_HIDE_MS = 600000;

let hideTimer: ReturnType<typeof setTimeout> | null = null;
let layoutListenersBound = false;
let floatingRafId = 0;

function normalizeMessage(message: string): string {
  return String(message ?? '')
    .trim()
    .replace(/\s*\n+\s*/g, ' · ');
}

function notifyStackTop(): number {
  const headerEl = document.querySelector('.site-header');
  let top =
    headerEl instanceof HTMLElement ? headerEl.offsetHeight : 0;
  const cart = document.getElementById('cart-summary-floating');
  if (
    cart &&
    !cart.classList.contains('is-hidden-floating') &&
    cart.offsetHeight > 0
  ) {
    top += cart.offsetHeight;
  }
  return top;
}

function updateTop(notify: HTMLElement): void {
  notify.style.top = `${notifyStackTop()}px`;
}

export function syncSfNotifyTop(): void {
  const notify = document.getElementById('sf-notify');
  if (!notify || notify.classList.contains('is-hidden')) return;
  updateTop(notify);
}

function scheduleSyncTop(): void {
  if (floatingRafId) return;
  floatingRafId = requestAnimationFrame(() => {
    floatingRafId = 0;
    syncSfNotifyTop();
  });
}

function bindLayoutListeners(): void {
  if (layoutListenersBound) return;
  layoutListenersBound = true;
  window.addEventListener('scroll', scheduleSyncTop, { passive: true });
  window.addEventListener('resize', scheduleSyncTop, { passive: true });
}

function clearHideTimer(): void {
  if (hideTimer) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
}

function hideNotify(notify: HTMLElement): void {
  clearHideTimer();
  notify.classList.add('is-hidden');
}

function restartHideTimer(notify: HTMLElement): void {
  clearHideTimer();
  hideTimer = setTimeout(() => {
    if (notify.isConnected) {
      hideNotify(notify);
    }
  }, AUTO_HIDE_MS);
}

function variantToDataType(variant: FlashVariant): 'error' | 'success' {
  return variant === 'error' ? 'error' : 'success';
}

function applyNotifyA11y(notify: HTMLElement, dataType: 'error' | 'success'): void {
  notify.setAttribute('data-type', dataType);
  notify.setAttribute('role', dataType === 'error' ? 'alert' : 'status');
  notify.setAttribute('aria-live', dataType === 'error' ? 'assertive' : 'polite');
  notify.setAttribute('aria-atomic', 'true');
}

function ensureNotifyStrip(): HTMLElement {
  let notify = document.getElementById('sf-notify');
  if (notify) return notify;

  notify = document.createElement('section');
  notify.id = 'sf-notify';
  notify.className = 'sf-notify is-hidden';
  applyNotifyA11y(notify, 'success');

  const container = document.createElement('div');
  container.className = 'container';

  const textEl = document.createElement('span');
  textEl.id = 'sf-notify-text';
  container.appendChild(textEl);

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'sf-notify__close';
  close.setAttribute('aria-label', 'Закрыть');
  close.textContent = '×';
  container.appendChild(close);

  notify.appendChild(container);
  document.body.appendChild(notify);

  bindCloseButton(notify);
  bindLayoutListeners();

  return notify;
}

function bindCloseButton(notify: HTMLElement): void {
  const btn = notify.querySelector('.sf-notify__close');
  if (!btn || (btn as HTMLElement).dataset.sfBound === '1') return;
  (btn as HTMLElement).dataset.sfBound = '1';
  btn.addEventListener('click', () => {
    hideNotify(notify);
  });
}

function revealNotify(notify: HTMLElement, onShown?: () => void): void {
  bindLayoutListeners();

  if (!notify.classList.contains('is-hidden')) {
    updateTop(notify);
    onShown?.();
    return;
  }

  updateTop(notify);
  void notify.offsetWidth;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (!notify.isConnected) return;
      notify.classList.remove('is-hidden');
      onShown?.();
    });
  });
}

export function showFlash(
  message: string,
  variant: FlashVariant = 'info',
  _durationMs?: number,
): void {
  const text = normalizeMessage(message);
  if (text === '') return;

  const notify = ensureNotifyStrip();
  bindCloseButton(notify);

  const dataType = variantToDataType(variant);
  applyNotifyA11y(notify, dataType);

  const textEl = document.getElementById('sf-notify-text');
  if (textEl) {
    textEl.textContent = text;
    textEl.title = text;
  }

  revealNotify(notify, () => {
    restartHideTimer(notify);
  });
}

export function showToast(message: string, _duration?: number): void {
  showFlash(message, 'info');
}

function pickDomMessage(notify: HTMLElement): string {
  const textEl = notify.querySelector('#sf-notify-text');
  return textEl?.textContent?.trim() ?? '';
}

export function initSfFlashFromDom(): void {
  const notify = document.getElementById('sf-notify');
  if (!notify) return;

  bindCloseButton(notify);
  const text = pickDomMessage(notify);
  if (text === '' || notify.classList.contains('is-hidden')) {
    return;
  }

  const domType = notify.getAttribute('data-type') === 'error' ? 'error' : 'success';
  applyNotifyA11y(notify, domType);

  notify.classList.add('is-hidden');
  revealNotify(notify, () => {
    bindLayoutListeners();
    restartHideTimer(notify);
  });
}

declare global {
  interface Window {
    showStorefrontFlash?: typeof showFlash;
  }
}

export function mountStorefrontFlashApi(): void {
  window.showStorefrontFlash = showFlash;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initSfFlashFromDom();
    });
  } else {
    initSfFlashFromDom();
  }
}
